import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import Select from 'react-select'
import { useTranslation } from 'react-i18next'

import TextAreaField from '../../../../FormLegacy/FormFieldsV2/TextAreaField'
import ToggleField from '../../../../FormLegacy/FormFieldsV2/ToggleField'
import Stepper from '../../../../Stepper/Stepper'
import API from '../../../../../api/api'
import { useSnackbar } from '../../../../../helpers/hooks/useSnackbar'
import ScheduledDateTimeFields from '../../../ScheduledDateTimeFields/ScheduledDateTimeFields'
import { useScheduledDateTime } from '../../../ScheduledDateTimeFields/useScheduledDateTime'

const INITIAL_FORM_STATE = {
  priority: null,
  category: null,
  location: null,
  areas: [],
  description: '',
  pets: false,
  permissionToEnter: true,
}

const ResmanSettings = ({
  building,
  resident,
  assignee,
  onBack,
  onCancel,
  voicemailChatMessageSid,
  inspectionId,
  description,
  checklistRoomItemId,
  onSuccess,
}) => {
  const { t } = useTranslation(['maintenance', 'common'])
  const scheduledDateTime = useScheduledDateTime({ timezone: building.timezone })
  const { displaySnackbar } = useSnackbar()
  const [formState, setFormState] = useState(INITIAL_FORM_STATE)
  const [resmanConfig, setResmanConfig] = useState({
    areas: [],
    categories: [],
    priorities: [],
    locations: [],
  })

  const handleFieldChange = ({ target: { value, name } }) => {
    setFormState((prev) => ({ ...prev, [name]: value }))
  }

  const handleToggleChange = ({ target: { name, checked } }) => {
    setFormState((prev) => ({ ...prev, [name]: checked }))
  }

  const handleSubmit = async () => {
    if (!scheduledDateTime.validate()) return

    const requestData = {
      buildingId: building.id,
      resman: true,
      description: formState.description,
      enterPermission: formState.permissionToEnter,
      pets: formState.pets,
      ...(formState.category && { sourceCategoryId: formState.category.value }),
      ...(formState.location && { sourceLocationId: formState.location.value }),
      ...(formState.areas && { location: formState.areas.map((area) => area.value) }),
      ...(formState.priority && { priority: formState.priority.value }),
      ...(assignee && { assignee: assignee.id }),
      ...(scheduledDateTime.timestamp && { scheduledDate: scheduledDateTime.timestamp }),
    }
    if (resident.clientId) {
      requestData.clientId = resident.clientId
    } else if (resident.unit) {
      requestData.unit = resident.unit
    }
    if (resident.sourceUnitId) {
      requestData.unitId = resident.sourceUnitId
    }
    if (voicemailChatMessageSid) {
      requestData.voicemailChatMessageSid = voicemailChatMessageSid
    }
    if (checklistRoomItemId) {
      requestData.checklistRoomItemId = checklistRoomItemId
    } else if (inspectionId) {
      requestData.inspectionId = inspectionId
    }

    try {
      await API.createMaintenance(requestData)
      displaySnackbar(t('maintenance:form.pmsSettings.snackbar.created'))
      if (onSuccess) {
        onSuccess()
      }
    } catch {
      displaySnackbar(t('maintenance:form.pmsSettings.snackbar.error'), true)
    } finally {
      onCancel()
    }
  }

  const areAllFieldsValid =
    !!formState.description && !!formState.priority && !!formState.location && !!formState.category

  useEffect(() => {
    ;(async () => {
      try {
        const resp = await API.getResmanConfig(building.id)
        const categoriesOptions = resp.data.categories.map((category) => ({
          value: category.categoryId,
          label: category.name,
        }))
        const areasOptions = resp.data.areas.map((area) => ({
          value: area,
          label: area,
        }))
        const locationsOptions = resp.data.locations.map((location) => ({
          value: location.id,
          label: location.name,
        }))
        const prioritiesOptions = resp.data.priorities.map((priority) => ({
          value: priority,
          label: priority,
        }))
        setResmanConfig({
          categories: categoriesOptions,
          locations: locationsOptions,
          areas: areasOptions,
          priorities: prioritiesOptions,
        })
      } catch {
        displaySnackbar(t('maintenance:form.resman.errors.couldNotLoadConfig'), true)
      }
    })()
  }, [building, building.id])

  useEffect(() => {
    if (description) {
      setFormState((prev) => ({ ...prev, description }))
    }
  }, [description])

  useEffect(() => {
    if (resident && resident.unit && resmanConfig.locations.length > 0) {
      const selectedLocation = resmanConfig.locations.find(
        (location) => location.label === resident.unit,
      )

      if (selectedLocation) {
        setFormState((prev) => ({ ...prev, location: selectedLocation }))
      }
    }
  }, [resident, resmanConfig.locations])

  return (
    <div className="maintenance-request-modal">
      <Stepper
        steps={[
          t('maintenance:form.steps.requestDetails'),
          t('maintenance:form.steps.requestSettings'),
        ]}
        currentStep={1}
      />
      <div className="row">
        <div className="row-header">{t('maintenance:form.resman.selectPriority')}</div>
        <Select
          className="select-container"
          classNamePrefix="select-primary"
          options={resmanConfig.priorities}
          name="priority"
          isClearable
          clearValue={() => setFormState((prev) => ({ ...prev, priority: null }))}
          value={formState.priority}
          onChange={(e) => setFormState((prev) => ({ ...prev, priority: e }))}
          placeholder={t('maintenance:form.resman.priorityPlaceholder')}
        />
      </div>
      <div className="row">
        <div className="row-header">{t('maintenance:form.pmsSettings.selectCategory')}</div>
        <Select
          className="select-container"
          classNamePrefix="select-primary"
          options={resmanConfig.categories}
          name="category"
          isClearable
          clearValue={() => setFormState((prev) => ({ ...prev, category: null }))}
          value={formState.category}
          onChange={(e) => setFormState((prev) => ({ ...prev, category: e }))}
          placeholder={t('maintenance:form.resman.categoryPlaceholder')}
        />
      </div>
      <div className="row">
        <div className="row-header">{t('maintenance:form.resman.selectArea')}</div>
        <Select
          className="select-container"
          classNamePrefix="select-primary"
          options={resmanConfig.areas}
          name="areas"
          isMulti
          isClearable
          clearValue={() => setFormState((prev) => ({ ...prev, areas: [] }))}
          value={formState.areas}
          onChange={(e) => setFormState((prev) => ({ ...prev, areas: e }))}
          placeholder={t('maintenance:form.resman.areaPlaceholder')}
        />
      </div>
      <div className="row">
        <ScheduledDateTimeFields {...scheduledDateTime.fieldProps} />
      </div>
      <div className="row">
        <div className="row-header">{t('maintenance:form.pmsSettings.requestDescription')}</div>
        <TextAreaField
          placeholder={t('maintenance:form.pmsSettings.descriptionPlaceholder')}
          name="description"
          value={formState.description}
          maxLength={250}
          onChange={handleFieldChange}
        />
      </div>
      <div className="row checkboxes">
        <div className="input-container">
          <ToggleField
            value={formState.pets}
            name="pets"
            label={t('maintenance:form.pmsSettings.pets')}
            labelOnRight
            onChange={handleToggleChange}
          />
        </div>
        <div className="input-container">
          <ToggleField
            name="permissionToEnter"
            label={t('maintenance:form.pmsSettings.permissionToEnter')}
            labelOnRight
            value={formState.permissionToEnter}
            onChange={handleToggleChange}
          />
        </div>
      </div>
      <div className="modal-footer">
        <div className="buttons">
          <button className="button button-gray" onClick={onBack}>
            {t('maintenance:form.pmsSettings.back')}
          </button>
          <button className="button button-gray" onClick={onCancel}>
            {t('common:actions.cancel')}
          </button>
          <button
            className="button button-primary"
            disabled={!areAllFieldsValid}
            onClick={handleSubmit}
          >
            {t('maintenance:form.pmsSettings.createRequest')}
          </button>
        </div>
      </div>
    </div>
  )
}

ResmanSettings.propTypes = {
  building: PropTypes.object,
  resident: PropTypes.object,
  assignee: PropTypes.object,
  onBack: PropTypes.func,
  onCancel: PropTypes.func,
  voicemailChatMessageSid: PropTypes.bool,
  inspectionId: PropTypes.number,
  description: PropTypes.string,
  checklistRoomItemId: PropTypes.number,
  onSuccess: PropTypes.func,
}

export default ResmanSettings
